import { useState, useRef, useCallback, useMemo, useEffect, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BookSquareOffAddMoreFlow from "../../components/BookSquareOffAddMoreFlow/BookSquareOffAddMoreFlow";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import { getStrategies, getStrategyReport } from "../../api/strategyReport";
import {
  STRATEGY_SEARCH_LAYOUT,
  buildStrategyCardsViewModel,
  buildStrategyReportViewModel,
} from "./strategyReportData";
import styles from "./StrategyReportPage.module.css";

const INITIAL_SEARCH = {
  scrip: "",
  ceStrike: "",
  peStrike: "",
  mpErosion: "",
};

const INITIAL_TABLE_FILTERS = {
  clientCodes: [],
  profitPct: "",
  individualLegErosion: "",
  maxProfitErosion: "",
  percentAway: "",
};

const TABLE_FILTER_FIELDS = [
  { key: "profitPct", label: "Profit %", placeholder: "Search PnL %" },
  { key: "individualLegErosion", label: "Individual Leg Erosion", placeholder: "Search leg erosion" },
  { key: "maxProfitErosion", label: "Max Profit Erosion", placeholder: "Search MP erosion" },
  { key: "percentAway", label: "% Away", placeholder: "Search % away" },
];

const getInfinitySymbol = (value) => {
  if (value === Infinity) return "∞";
  if (value === -Infinity) return "-∞";
  const normalizedValue = String(value).toLowerCase();
  if (normalizedValue === "∞" || normalizedValue === "inf" || normalizedValue === "infinity") return "∞";
  if (normalizedValue === "-∞" || normalizedValue === "-inf" || normalizedValue === "-infinity") return "-∞";
  return null;
};

const isInfiniteValue = (value) => getInfinitySymbol(value) != null;

const formatPnl = (value) => {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString("en-IN");
  return value >= 0 ? `+${formatted}` : `-${formatted}`;
};

const formatNumber = (value) => {
  if (value == null || value === "") return "";
  if (isInfiniteValue(value)) return getInfinitySymbol(value);
  return typeof value === "number" ? value.toLocaleString("en-IN") : value;
};

const formatPercentDisplay = (value) => {
  if (value == null || value === "") return "";
  if (isInfiniteValue(value)) return getInfinitySymbol(value);
  return `${formatNumber(value)}%`;
};

const formatBreakeven = (strike, pct) => {
  if (strike == null || strike === "") return "";
  if (pct == null || pct === "") return formatNumber(strike);
  return `${formatNumber(strike)} (${formatPercentDisplay(pct)})`;
};

const formatBreakevenList = (points = [], pctAway = []) => {
  const safePoints = Array.isArray(points) ? points : [];
  const safePctAway = Array.isArray(pctAway) ? pctAway : [];
  if (!safePoints.length) return [];
  return safePoints.map((point, index) => formatBreakeven(point, safePctAway[index]));
};

const normalizeStrike = (value) => String(Number(value));

const pnlToneClass = (value, stylesModule) =>
  Number(value) >= 0 ? stylesModule.pnlPositive : stylesModule.pnlNegative;

const matchesField = (value, query) => {
  if (!query.trim()) return true;
  return String(value).toLowerCase().includes(query.trim().toLowerCase());
};

const matchesStrikeFilter = (strikes = [], query) => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return true;

  const comparisonMatch = trimmedQuery.match(/^([<>])\s*(\d+(?:\.\d+)?)$/);
  if (comparisonMatch) {
    const [, operator, rawValue] = comparisonMatch;
    const compareValue = Number(rawValue);
    return strikes.some((strike) => {
      const numericStrike = Number(strike);
      if (!Number.isFinite(numericStrike)) return false;
      return operator === ">" ? numericStrike > compareValue : numericStrike < compareValue;
    });
  }

  const exactValue = Number(trimmedQuery);
  if (Number.isFinite(exactValue)) {
    return strikes.some((strike) => Number(strike) === exactValue);
  }

  return matchesField(strikes.join(","), trimmedQuery);
};

const isActiveFilterValue = (value) => (Array.isArray(value) ? value.length > 0 : String(value).trim());

const hasActiveFilters = (filters) => Object.values(filters).some(isActiveFilterValue);

const getActiveFilterCount = (filters) => Object.values(filters).filter(isActiveFilterValue).length;

const getSelectedClientLabel = (clientCodes) => {
  if (!clientCodes.length) return "All clients";
  if (clientCodes.length === 1) return clientCodes[0];
  return `${clientCodes.length} clients selected`;
};

const matchesFilterValue = (value, query) => {
  if (!query.trim()) return true;
  return matchesField(value, query) || matchesField(formatPercentDisplay(value), query);
};

const groupMatchesTableFilters = (group, detail, filters) => {
  if (filters.clientCodes.length > 0 && !filters.clientCodes.includes(group.clientCode)) return false;

  if (
    filters.profitPct.trim() &&
    !matchesFilterValue(group.totals?.profitPct, filters.profitPct) &&
    !group.rows.some((row) => matchesFilterValue(row.profitPct, filters.profitPct))
  ) {
    return false;
  }

  if (
    filters.individualLegErosion.trim() &&
    !matchesFilterValue(group.individualLegErosion, filters.individualLegErosion) &&
    !group.rows.some((row) => matchesFilterValue(row.individualLegErosion, filters.individualLegErosion))
  ) {
    return false;
  }

  if (
    filters.maxProfitErosion.trim() &&
    !matchesFilterValue(group.maxProfitErosion, filters.maxProfitErosion) &&
    !matchesFilterValue(detail.maxProfitErosion, filters.maxProfitErosion)
  ) {
    return false;
  }

  if (
    filters.percentAway.trim() &&
    !group.breakevenPctAway?.some((pct) => matchesFilterValue(pct, filters.percentAway))
  ) {
    return false;
  }

  return true;
};

const filterClientGroups = (groups, detail, filters) => {
  if (!hasActiveFilters(filters)) return groups;
  return groups.filter((group) => groupMatchesTableFilters(group, detail, filters));
};

const getErrorMessage = (error) =>
  error?.response?.data?.detail || error?.message || "Unable to load strategy report.";

function StrategyWaveIcon({ color }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M2 14C4.5 10 6 8 10 8C14 8 15.5 10 18 14"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M2 10C4.5 6 6 4 10 4C14 4 15.5 6 18 10"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.5"
      />
    </svg>
  );
}

function StrikeValues({ strikes = [], openStrikes = [] }) {
  if (!strikes.length) return <span>--</span>;

  const openStrikeSet = new Set(openStrikes.map(normalizeStrike));

  return (
    <span className={styles.strikeValues}>
      {strikes.map((strike, index) => {
        const isOpen = openStrikeSet.has(normalizeStrike(strike));
        return (
          <Fragment key={`${strike}-${index}`}>
            {index > 0 && <span className={styles.strikeSeparator}>, </span>}
            <span className={isOpen ? styles.openStrikeValue : undefined}>{formatNumber(strike)}</span>
          </Fragment>
        );
      })}
    </span>
  );
}

function StrategyReportPage() {
  const { strategyId } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(() => ({
    strategyCards: [],
    detailItems: [],
  }));
  const [selectedId, setSelectedId] = useState(strategyId ?? null);
  const [selectedDetailId, setSelectedDetailId] = useState(null);
  const [expandedDetailIds, setExpandedDetailIds] = useState(() => new Set());
  const [searchFilters, setSearchFilters] = useState(INITIAL_SEARCH);
  const [tableFilters, setTableFilters] = useState(INITIAL_TABLE_FILTERS);
  const [isTableFilterOpen, setIsTableFilterOpen] = useState(false);
  const [isClientFilterOpen, setIsClientFilterOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [sqOffOpenToken, setSqOffOpenToken] = useState(0);
  const [isStrategiesLoading, setIsStrategiesLoading] = useState(false);
  const [strategiesError, setStrategiesError] = useState("");
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const cardsRef = useRef(null);
  const cardRefs = useRef({});
  const hasLoadedStrategyCardsRef = useRef(false);
  const initialStrategyIdRef = useRef(strategyId);
  const strategyCards = reportData.strategyCards;
  const detailItems = reportData.detailItems;

  const filteredDetailItems = useMemo(() => {
    return detailItems.filter((item) => {
      if (!matchesField(item.scripName, searchFilters.scrip)) return false;
      if (!matchesStrikeFilter(item.ceStrikes, searchFilters.ceStrike)) return false;
      if (!matchesStrikeFilter(item.peStrikes, searchFilters.peStrike)) return false;
      if (!matchesField(item.maxProfitErosion, searchFilters.mpErosion)) return false;
      if (hasActiveFilters(tableFilters) && !filterClientGroups(item.clientGroups, item, tableFilters).length) return false;
      return true;
    });
  }, [detailItems, searchFilters, tableFilters]);

  const clientFilterOptions = useMemo(() => {
    const clientCodes = detailItems.flatMap((item) => item.clientGroups.map((group) => group.clientCode));
    return Array.from(new Set(clientCodes.filter(Boolean))).sort();
  }, [detailItems]);

  const selectedClientLabel = getSelectedClientLabel(tableFilters.clientCodes);

  const selectedDetail =
    filteredDetailItems.find((item) => item.id === selectedDetailId) ||
    filteredDetailItems[0] ||
    null;
  const visibleClientGroups = selectedDetail ? filterClientGroups(selectedDetail.clientGroups, selectedDetail, tableFilters) : [];
  const visiblePositionRows = visibleClientGroups.flatMap((group) => group.rows);
  const selectedSqOffRows = useMemo(
    () => visiblePositionRows.filter((row) => selectedRows.has(row.id)),
    [visiblePositionRows, selectedRows]
  );

  const scrollSelectedCardIntoView = useCallback((cardId) => {
    const container = cardsRef.current;
    const cardEl = cardRefs.current[cardId];
    if (!container || !cardEl) return;

    const targetScrollLeft =
      cardEl.offsetLeft - (container.clientWidth - cardEl.offsetWidth) / 2;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    container.scrollTo({
      left: Math.min(Math.max(targetScrollLeft, 0), maxScrollLeft),
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!hasLoadedStrategyCardsRef.current) {
      setIsStrategiesLoading(true);
    }
    setStrategiesError("");

    getStrategies()
      .then((response) => {
        if (!isMounted) return;
        const cards = buildStrategyCardsViewModel(response);
        if (!cards.length) return;

        hasLoadedStrategyCardsRef.current = true;
        setReportData((prev) => ({ ...prev, strategyCards: cards }));

        let shouldNavigateToFallback = false;
        setSelectedId((current) => {
          if (current && cards.some((card) => card.id === current)) {
            return current;
          }

          shouldNavigateToFallback = !initialStrategyIdRef.current;
          return initialStrategyIdRef.current || cards[0].id;
        });

        if (shouldNavigateToFallback) {
          navigate(`/strategy-report/${cards[0].id}`, { replace: true });
        }
      })
      .catch((error) => {
        if (isMounted) setStrategiesError(getErrorMessage(error));
      })
      .finally(() => {
        if (isMounted) setIsStrategiesLoading(false);
      });

    return () => {
      isMounted = false;
    };
    // Fetch strategy cards once; card selection only updates the report via selectedId.
  }, [navigate]);

  useEffect(() => {
    if (strategyId) {
      setSelectedId(strategyId);
    }
  }, [strategyId]);

  useEffect(() => {
    if (!selectedId || isStrategiesLoading || strategyCards.length === 0) return undefined;

    const frameId = requestAnimationFrame(() => {
      scrollSelectedCardIntoView(selectedId);
    });

    return () => cancelAnimationFrame(frameId);
  }, [selectedId, isStrategiesLoading, strategyCards.length, scrollSelectedCardIntoView]);

  useEffect(() => {
    if (!selectedId) return undefined;

    let isMounted = true;
    setIsReportLoading(true);
    setReportError("");
    setReportData((prev) => ({ ...prev, detailItems: [] }));
    setSelectedDetailId(null);
    setExpandedDetailIds(new Set());
    setSelectedRows(new Set());
    setSearchFilters(INITIAL_SEARCH);
    setTableFilters(INITIAL_TABLE_FILTERS);
    setIsClientFilterOpen(false);

    getStrategyReport(selectedId)
      .then((response) => {
        if (!isMounted) return;
        const viewModel = buildStrategyReportViewModel(response);
        setReportData((prev) => ({ ...prev, detailItems: viewModel.detailItems }));
        setSelectedDetailId(viewModel.detailItems[0]?.id ?? null);
        setExpandedDetailIds(new Set());
        setSelectedRows(new Set());
        setTableFilters(INITIAL_TABLE_FILTERS);
      })
      .catch((error) => {
        if (!isMounted) return;
        setReportData((prev) => ({ ...prev, detailItems: [] }));
        setSelectedDetailId(null);
        setExpandedDetailIds(new Set());
        setSelectedRows(new Set());
        setTableFilters(INITIAL_TABLE_FILTERS);
        setReportError(getErrorMessage(error));
      })
      .finally(() => {
        if (isMounted) setIsReportLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedId]);

  useEffect(() => {
    if (filteredDetailItems.length === 0) {
      setSelectedDetailId(null);
      setExpandedDetailIds(new Set());
      return;
    }
    const stillVisible = filteredDetailItems.some((item) => item.id === selectedDetailId);
    if (!stillVisible) {
      setSelectedDetailId(filteredDetailItems[0].id);
    }
    const visibleIds = new Set(filteredDetailItems.map((item) => item.id));
    setExpandedDetailIds((prev) => {
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredDetailItems, selectedDetailId]);

  const scrollCards = useCallback((direction) => {
    if (!cardsRef.current) return;
    cardsRef.current.scrollBy({ left: direction * 240, behavior: "smooth" });
  }, []);

  const handleCardsWheel = useCallback((event) => {
    const container = cardsRef.current;
    if (!container) return;

    const scrollDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (!scrollDelta) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) return;

    const nextScrollLeft = Math.min(Math.max(container.scrollLeft + scrollDelta, 0), maxScrollLeft);
    if (nextScrollLeft === container.scrollLeft) return;

    event.preventDefault();
    container.scrollLeft = nextScrollLeft;
  }, []);

  const handleSearchChange = (key, value) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleTableFilterChange = (key, value) => {
    setTableFilters((prev) => ({ ...prev, [key]: value }));
    setSelectedRows(new Set());
  };

  const toggleClientFilter = (clientCode) => {
    setTableFilters((prev) => {
      const isSelected = prev.clientCodes.includes(clientCode);
      return {
        ...prev,
        clientCodes: isSelected
          ? prev.clientCodes.filter((code) => code !== clientCode)
          : [...prev.clientCodes, clientCode],
      };
    });
    setSelectedRows(new Set());
  };

  const clearTableFilters = () => {
    setTableFilters(INITIAL_TABLE_FILTERS);
    setIsClientFilterOpen(false);
    setSelectedRows(new Set());
  };

  const handleTableFilterToggle = () => {
    if (isTableFilterOpen) setIsClientFilterOpen(false);
    setIsTableFilterOpen((prev) => !prev);
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllRows = () => {
    if (visiblePositionRows.length > 0 && selectedRows.size === visiblePositionRows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(visiblePositionRows.map((r) => r.id)));
    }
  };

  const hasSelectedRows = selectedSqOffRows.length > 0;

  const handleRollOver = () => {
    if (!hasSelectedRows) return;
    console.log("Roll Over clicked", Array.from(selectedRows));
  };

  const handleReprice = () => {
    if (!hasSelectedRows) return;
    console.log("Reprice clicked", Array.from(selectedRows));
  };

  const handleSqOff = () => {
    if (!hasSelectedRows) return;
    setSqOffOpenToken((prev) => prev + 1);
  };

  const handleDetailCardClick = (itemId) => {
    setSelectedDetailId(itemId);
    setSelectedRows(new Set());
    setExpandedDetailIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleCarouselSelect = (cardId) => {
    if (cardId === selectedId) return;
    setSelectedId(cardId);
    setSelectedRows(new Set());
    setSearchFilters(INITIAL_SEARCH);
    setTableFilters(INITIAL_TABLE_FILTERS);
    setExpandedDetailIds(new Set());
    navigate(`/strategy-report/${cardId}`);
  };

  return (
    <div className={styles.page}>
      <div className={styles.strategiesSection}>
        <div className={styles.strategiesHeader}>
          <div>
            <h1 className={styles.strategiesTitle}>Strategies</h1>
            <p className={styles.strategiesSubtitle}>Click a strategy to view details</p>
          </div>
        </div>

        <div className={styles.cardsRow}>
          <div className={styles.cardsScroll} ref={cardsRef} onWheel={handleCardsWheel}>
            <div className={styles.cardsScrollTrack}>
              {isStrategiesLoading ? (
                <div className={styles.strategyCardsMessage}>Loading strategies...</div>
              ) : strategiesError ? (
                <div className={styles.strategyCardsMessage}>{strategiesError}</div>
              ) : strategyCards.length === 0 ? (
                <div className={styles.strategyCardsMessage}>No strategies found.</div>
              ) : (
                strategyCards.map((card) => {
              const isSelected = card.id === selectedId;
              const cardPnlPositive = card.pnl >= 0;
              return (
                <button
                  key={card.id}
                  type="button"
                    ref={(node) => {
                      if (node) cardRefs.current[card.id] = node;
                      else delete cardRefs.current[card.id];
                    }}
                  className={`${styles.strategyCard} ${isSelected ? styles.strategyCardSelected : ""}`}
                    onClick={() => handleCarouselSelect(card.id)}
                >
                    <div className={styles.cardTopRow}>
                  <div className={styles.cardIconWrap} style={{ backgroundColor: card.iconBg }}>
                    <StrategyWaveIcon color={card.iconColor} />
                  </div>
                  <span className={styles.cardName}>{card.name}</span>
                    </div>
                  <span className={`${styles.cardPnl} ${cardPnlPositive ? styles.pnlPositive : styles.pnlNegative}`}>
                      <span className={styles.cardPnlIconWrap}>
                        <GlobalIcon
                          iconName={cardPnlPositive ? "ProfitArrowIcon" : "LossArrowIcon"}
                          className={styles.cardPnlIcon}
                        />
                  </span>
                      <span>{formatPnl(card.pnl)}</span>
                  </span>
                </button>
              );
              }))}
            </div>
          </div>
          <button type="button" className={styles.cardsScrollBtn} onClick={() => scrollCards(1)} aria-label="Scroll strategies">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <div className={styles.reportColumnsWrap}>
        <div className={styles.pageToolbar}>
          <button
            type="button"
            className={`${styles.filterBtn} ${isTableFilterOpen ? styles.filterBtnActive : ""}`}
            onClick={handleTableFilterToggle}
            aria-expanded={isTableFilterOpen}
          >
            <GlobalIcon iconName="FilterIcon" className={styles.filterIcon} />
            Filter
            {hasActiveFilters(tableFilters) && <span className={styles.filterCount}>{getActiveFilterCount(tableFilters)}</span>}
          </button>
          <div className={styles.pageActions}>
            <button type="button" className={styles.outlineBtn} onClick={handleRollOver} disabled={!hasSelectedRows}>
              <GlobalIcon iconName="RepeatIcon" className={styles.actionIcon} />
              Roll Over
            </button>
            <button type="button" className={styles.outlineBtn} onClick={handleReprice} disabled={!hasSelectedRows}>
              <GlobalIcon iconName="RefreshIcon" className={styles.actionIcon} />
              Reprice
            </button>
            <button type="button" className={styles.sqOffBtn} onClick={handleSqOff} disabled={!hasSelectedRows}>
              <GlobalIcon iconName="ToggleOnIcon" className={styles.sqOffIcon} />
              SQ OFF
            </button>
          </div>
        </div>

        <BookSquareOffAddMoreFlow
          bookType="position"
          selectedRowsData={selectedSqOffRows}
          selectedCount={selectedSqOffRows.length}
          hideActions
          openSqOffToken={sqOffOpenToken}
        />

        {isTableFilterOpen && (
          <div className={styles.tableFilterPanel}>
            <div className={styles.tableFilterHeader}>
              <div>
                <p className={styles.tableFilterTitle}>Inside Summary Table Filters</p>
                <p className={styles.tableFilterHint}>Matches values inside client groups and batch positions.</p>
              </div>
              <button
                type="button"
                className={styles.clearFilterBtn}
                onClick={clearTableFilters}
                disabled={!hasActiveFilters(tableFilters)}
              >
                Clear
              </button>
            </div>
            <div className={styles.tableFilterGrid}>
              <div className={styles.tableFilterField}>
                <span className={styles.tableFilterLabel}>Client Code</span>
                <div className={styles.clientFilterDropdown}>
                  <button
                    type="button"
                    className={styles.clientFilterButton}
                    onClick={() => setIsClientFilterOpen((prev) => !prev)}
                    aria-expanded={isClientFilterOpen}
                  >
                    <span>{selectedClientLabel}</span>
                    <GlobalIcon iconName="ChevronDownIcon" className={styles.clientFilterChevron} />
                  </button>
                  {isClientFilterOpen && (
                    <div className={styles.clientFilterMenu}>
                      {clientFilterOptions.length === 0 ? (
                        <div className={styles.clientFilterEmpty}>No clients</div>
                      ) : (
                        clientFilterOptions.map((clientCode) => (
                          <label key={clientCode} className={styles.clientFilterOption}>
                            <input
                              type="checkbox"
                              className={styles.checkbox}
                              checked={tableFilters.clientCodes.includes(clientCode)}
                              onChange={() => toggleClientFilter(clientCode)}
                            />
                            <span>{clientCode}</span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              {TABLE_FILTER_FIELDS.map((field) => (
                <div key={field.key} className={styles.tableFilterField}>
                  <span className={styles.tableFilterLabel}>{field.label}</span>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder={field.placeholder}
                    value={tableFilters[field.key]}
                    onChange={(e) => handleTableFilterChange(field.key, e.target.value)}
                    aria-label={field.label}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={`${styles.searchBar} ${styles.metricsGrid}`}>
          {STRATEGY_SEARCH_LAYOUT.map((col) =>
            col.type === "spacer" ? (
              <div key={col.id} className={styles.searchSpacer} aria-hidden="true" />
            ) : (
              <div
                key={col.key}
                className={`${styles.searchField} ${
                  col.columnType === "numeric" ? styles.searchFieldNumeric : styles.searchFieldText
                }`}
              >
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={col.placeholder}
                  value={searchFilters[col.key]}
                  onChange={(e) => handleSearchChange(col.key, e.target.value)}
                  aria-label={col.label}
                />
              </div>
            )
          )}
            </div>

        <div className={styles.detailCardsList}>
        {isReportLoading ? (
          <div className={styles.emptySearch}>Loading strategy report...</div>
        ) : reportError ? (
          <div className={styles.emptySearch}>{reportError}</div>
        ) : filteredDetailItems.length === 0 ? (
          <div className={styles.emptySearch}>No strategies match your search.</div>
        ) : (
          filteredDetailItems.map((detail) => {
            const isSelected = detail.id === selectedDetailId;
            const isExpanded = expandedDetailIds.has(detail.id);
            const detailClientGroups = filterClientGroups(detail.clientGroups, detail, tableFilters);
            const detailPositionRows = detailClientGroups.flatMap((group) => group.rows);
            const isActiveDetail = detail.id === selectedDetailId;
            return (
              <div
                key={detail.id}
                className={`${styles.detailPanel} ${styles.detailPanelCompact} ${
                  isSelected ? styles.detailPanelSelected : ""
                } ${!isExpanded ? styles.detailPanelCollapsed : ""}`}
              >
                <button
                  type="button"
                  className={styles.detailHeaderBtn}
                  onClick={() => handleDetailCardClick(detail.id)}
                  aria-expanded={isExpanded}
                >
                  <div className={styles.metricsGrid} role="group" aria-label="Strategy metrics">
                    <div className={`${styles.metricItem} ${styles.metricItemText}`} title={detail.scripName}>
                      <span className={styles.metricLabel}>Scrip</span>
                      <span className={`${styles.metricValue} ${styles.metricValueText}`}>{detail.scripName}</span>
                    </div>
                    <div className={`${styles.metricItem} ${styles.metricItemNumeric}`}>
                      <span className={styles.metricLabel}>CMP</span>
                      <span className={`${styles.metricValue} ${styles.metricValueNumeric}`}>{formatNumber(detail.cmp)}</span>
                    </div>
                    <div className={`${styles.metricItem} ${styles.metricItemNumeric}`}>
                      <span className={styles.metricLabel}>Futures</span>
                      <span className={`${styles.metricValue} ${styles.metricValueNumeric}`}>{detail.future}</span>
                    </div>
                    <div className={`${styles.metricItem} ${styles.metricItemNumeric}`}>
                      <span className={styles.metricLabel}>CE Strike</span>
                      <span className={`${styles.metricValue} ${styles.metricValueNumeric}`}>
                        <StrikeValues strikes={detail.ceStrikes} openStrikes={detail.ceOpenStrikes} />
                      </span>
                    </div>
                    <div className={`${styles.metricItem} ${styles.metricItemNumeric}`}>
                      <span className={styles.metricLabel}>PE Strike</span>
                      <span className={`${styles.metricValue} ${styles.metricValueNumeric}`}>
                        <StrikeValues strikes={detail.peStrikes} openStrikes={detail.peOpenStrikes} />
                      </span>
                    </div>
                    <div className={`${styles.metricItem} ${styles.metricItemNumeric}`}>
                      <span className={styles.metricLabel}>Lots</span>
                      <span className={`${styles.metricValue} ${styles.metricValueNumeric}`}>{detail.lots}</span>
                    </div>
                    <div className={`${styles.metricItem} ${styles.metricItemErosion}`}>
                      <span className={styles.metricLabel}>Max Profit Erosion</span>
                      <span
                        className={`${styles.metricValue} ${styles.metricValueErosion} ${pnlToneClass(
                          detail.maxProfitErosion,
                          styles
                        )}`}
                      >
                        {formatPercentDisplay(detail.maxProfitErosion)}
                      </span>
                    </div>
                    <div className={styles.detailCollapseCell}>
                      <span
                        className={`${styles.collapseBtn} ${styles.collapseBtnInline} ${
                          !isExpanded ? styles.collapseBtnRotated : ""
                        }`}
                        aria-hidden="true"
                      >
                        <GlobalIcon iconName="ChevronDownIcon" className={styles.collapseIcon} />
                      </span>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <>
              <div className={styles.tableWrap}>
                <table className={styles.positionsTable}>
                  <thead>
                          <tr>
                            <th className={styles.thCheckbox}>
                              <input
                                type="checkbox"
                                className={styles.checkbox}
                                checked={
                                  isActiveDetail &&
                                  detailPositionRows.length > 0 &&
                                  selectedRows.size === detailPositionRows.length
                                }
                                onChange={toggleAllRows}
                                disabled={!isActiveDetail}
                                aria-label="Select all rows"
                              />
                      </th>
                            <th>Client Code</th>
                            <th>Tran</th>
                            <th>Trading Symbol</th>
                            <th>Strike</th>
                            <th>SQ Off</th>
                            <th>Buy Price</th>
                            <th>Sell Price</th>
                            <th>Net Qty</th>
                            <th>CMP</th>
                            <th>Profit</th>
                            <th>Expiry PnL</th>
                            <th>PnL %</th>
                            <th>Max Profit</th>
                            <th>Max Loss</th>
                            <th>Breakeven %</th>
                    </tr>
                  </thead>
                  <tbody>
                          {detailClientGroups.map((group) => (
                            <Fragment key={`${group.clientCode}-${group.batchId}`}>
                              {group.rows.map((row, index) => (
                                <tr key={row.id}>
                                  <td className={styles.tdCheckbox}>
                                    <input
                                      type="checkbox"
                                      className={styles.checkbox}
                                      checked={isActiveDetail && selectedRows.has(row.id)}
                                      onChange={() => toggleRow(row.id)}
                                      disabled={!isActiveDetail}
                                      aria-label={`Select row ${row.id}`}
                                    />
                                  </td>
                                  {index === 0 && (
                                    <td rowSpan={group.rows.length} className={styles.clientCodeCell}>
                                      {group.clientCode}
                                    </td>
                                  )}
                                  <td>
                                    <span className={row.tran === "B" ? styles.tranBuy : styles.tranSell}>
                                      {row.tran}
                                    </span>
                                  </td>
                        <td className={styles.symbolCell}>{row.symbol}</td>
                                  <td>{formatNumber(row.strike)}</td>
                                  <td>{formatNumber(row.sqOff)}</td>
                                  <td>{formatNumber(row.buyPrice)}</td>
                                  <td>{formatNumber(row.sellPrice)}</td>
                                  <td>{formatNumber(row.netQty)}</td>
                                  <td>{formatNumber(row.cmp)}</td>
                                  <td className={`${styles.profitCell} ${pnlToneClass(row.profit, styles)}`}>
                                    {formatNumber(row.profit)}
                                  </td>
                                  <td className={`${styles.profitCell} ${pnlToneClass(row.expiryPnl, styles)}`}>
                                    {formatNumber(row.expiryPnl)}
                                  </td>
                                  <td className={`${styles.profitCell} ${pnlToneClass(row.profitPct, styles)}`}>
                                    {formatPercentDisplay(row.profitPct)}
                                  </td>
                                  {index === 0 && (
                                    <>
                                      <td rowSpan={group.rows.length} className={styles.groupCommonCell}>
                                        {formatNumber(group.maxProfit)}
                                      </td>
                                      <td rowSpan={group.rows.length} className={`${styles.groupCommonCell} ${styles.maxLossCell}`}>
                                        {formatNumber(group.maxLoss)}
                                      </td>
                                      <td rowSpan={group.rows.length} className={styles.groupCommonCell}>
                                        <span className={styles.breakevenList}>
                                          {(() => {
                                            const breakevenItems = formatBreakevenList(
                                              group.breakevenPoints,
                                              group.breakevenPctAway
                                            );
                                            if (!breakevenItems.length) return "—";
                                            return breakevenItems.map((value) => (
                                              <span key={value} className={styles.breakevenItem}>
                                                {value}
                                              </span>
                                            ));
                                          })()}
                                        </span>
                                      </td>
                                    </>
                                  )}
                      </tr>
                    ))}
                    <tr className={styles.totalRow}>
                                <td className={styles.tdCheckbox} />
                                <td className={styles.totalLabel}>Total</td>
                                <td colSpan={4} className={styles.batchIdCell}>
                                  <span className={styles.batchIdLabel}>Batch ID</span>
                                  <span className={styles.batchIdValue}>{group.batchId || "—"}</span>
                                </td>
                                <td />
                                <td>
                                  <span className={styles.totalValueBox}>{formatNumber(group.totals.sellPrice)}</span>
                                </td>
                                <td>
                                  <span className={`${styles.totalValueBox} ${styles.totalValueBoxDark}`}>
                                    {formatNumber(group.totals.netQty)}
                                  </span>
                                </td>
                                <td />
                                <td className={`${styles.profitCell} ${pnlToneClass(group.totals.profit, styles)}`}>
                                  <span
                                    className={`${styles.totalValueBox} ${pnlToneClass(group.totals.profit, styles)}`}
                                  >
                                    {formatNumber(group.totals.profit)}
                                  </span>
                                </td>
                                <td className={`${styles.profitCell} ${pnlToneClass(group.totals.expiryPnl, styles)}`}>
                                  <span
                                    className={`${styles.totalValueBox} ${pnlToneClass(group.totals.expiryPnl, styles)}`}
                                  >
                                    {formatNumber(group.totals.expiryPnl)}
                                  </span>
                                </td>
                                <td className={`${styles.profitCell} ${pnlToneClass(group.totals.profitPct, styles)}`}>
                                  <span
                                    className={`${styles.totalValueBox} ${pnlToneClass(group.totals.profitPct, styles)}`}
                                  >
                                    {formatPercentDisplay(group.totals.profitPct)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={`${styles.totalValueBox} ${pnlToneClass(
                                      group.totals.maxProfitErosion,
                                      styles
                                    )}`}
                                  >
                                    {formatPercentDisplay(group.totals.maxProfitErosion)}
                                  </span>
                                </td>
                                <td className={styles.maxLossCell}>
                                  <span
                                    className={`${styles.totalValueBox} ${pnlToneClass(
                                      group.totals.maxLossErosion,
                                      styles
                                    )}`}
                                  >
                                    {formatPercentDisplay(group.totals.maxLossErosion)}
                                  </span>
                                </td>
                                <td />
                    </tr>
                            </Fragment>
                          ))}
                  </tbody>
                </table>
              </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
      </div>
    </div>
  );
}

export default StrategyReportPage;
